import * as React from "react";
import { cx } from "../util.js";

export interface DisplayProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Visual size of the display serif. @default "lg" */
  size?: "lg" | "xl" | "2xl";
  as?: keyof React.JSX.IntrinsicElements;
}
/** Large Fraunces display type for hero/book/chapter titles. */
export function Display({ size = "lg", as: Tag = "h1", className, ...rest }: DisplayProps) {
  return <Tag className={cx("ip-display", className)} data-size={size} {...(rest as any)} />;
}

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Heading level 1–4. 1–2 use the serif, 3–4 use the sans. @default 2 */
  level?: 1 | 2 | 3 | 4;
}
/** Section/panel headings. */
export function Heading({ level = 2, className, ...rest }: HeadingProps) {
  const Tag = (`h${level}` as unknown) as keyof React.JSX.IntrinsicElements;
  return <Tag className={cx("ip-heading", className)} data-level={level} {...(rest as any)} />;
}

export interface TextProps extends React.HTMLAttributes<HTMLParagraphElement> {
  size?: "lg" | "md" | "sm" | "xs";
  tone?: "default" | "muted" | "subtle";
  as?: keyof React.JSX.IntrinsicElements;
}
/** Body / UI text in Inter. */
export function Text({ size = "md", tone = "default", as: Tag = "p", className, ...rest }: TextProps) {
  return (
    <Tag
      className={cx("ip-text", className)}
      data-size={size}
      data-tone={tone === "default" ? undefined : tone}
      {...(rest as any)}
    />
  );
}

export interface ReadingProps extends React.HTMLAttributes<HTMLDivElement> {}
/** Long-form manuscript surface in Newsreader, constrained to an ideal measure. */
export function Reading({ className, ...rest }: ReadingProps) {
  return <div className={cx("ip-reading", className)} {...rest} />;
}

export interface MonoProps extends React.HTMLAttributes<HTMLSpanElement> {}
/** Monospace text for counts, reading time, hashes. */
export function Mono({ className, ...rest }: MonoProps) {
  return <span className={cx("ip-mono", className)} {...rest} />;
}

export interface EyebrowProps extends React.HTMLAttributes<HTMLParagraphElement> {}
/** Small uppercase accent label above a heading. */
export function Eyebrow({ className, ...rest }: EyebrowProps) {
  return <p className={cx("ip-eyebrow", className)} {...rest} />;
}
